import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SignIn, SignOutButton, useUser, useAuth } from "@clerk/react";
import { ShieldAlert, Radio, MapPin, Mic, Play, Pause, Download, RefreshCw, PhoneCall, User } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { fmtDT } from "@/lib/brand";
import { GoogleTripMap, MAPS_KEY } from "@/components/site/GoogleMaps";

const hideSocial = { elements: { socialButtonsRoot: { display: "none" }, dividerRow: { display: "none" }, footerAction: { display: "none" } } };

export const OfficerSignInPage = () => (
  <div className="grid min-h-screen place-items-center bg-[#0B1120] px-4" data-testid="officer-signin-page">
    <div className="w-full max-w-md text-center">
      <p className="mb-6 text-sm font-semibold uppercase tracking-[0.25em] text-sky-400">Security &amp; Compliance</p>
      <SignIn routing="path" path="/officer" signUpUrl="/officer" fallbackRedirectUrl="/officer" appearance={hideSocial} />
    </div>
  </div>
);

function LiveAudio({ bookingId, initialRecording }) {
  const api = useApi();
  const [chunks, setChunks] = useState([]);
  const [live, setLive] = useState(false);
  const [recording, setRecording] = useState(!!initialRecording);
  const [speed, setSpeed] = useState(1);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [err, setErr] = useState("");
  const [micPaused, setMicPaused] = useState(false);
  const [micError, setMicError] = useState("");
  const audioRef = useRef(null);
  const lastSeq = useRef(-1);
  const queue = useRef([]);
  const liveRef = useRef(false);
  const playingRef = useRef(false);
  const seen = useRef(new Set());

  const curUrl = useRef(null);
  const playBlob = async (ch) => {
    try {
      playingRef.current = true;
      setNowPlaying(ch.id);
      const r = await api.get(`/officer/audio/${ch.id}`, { responseType: "blob" });
      if (curUrl.current) { URL.revokeObjectURL(curUrl.current); }
      const url = URL.createObjectURL(r.data);
      curUrl.current = url;
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.load();
        audioRef.current.playbackRate = speed;
        try { await audioRef.current.play(); } catch { setErr("Tap the ▶ play control above to listen."); playingRef.current = false; }
      }
    } catch { playingRef.current = false; setNowPlaying(null); setErr("Couldn't load this audio segment."); }
  };
  const playNext = () => {
    if (playingRef.current || !queue.current.length) return;
    playBlob(queue.current.shift());
  };

  const poll = useCallback(async () => {
    try {
      const r = await api.get(`/officer/trips/${bookingId}?after_seq=${lastSeq.current}`);
      setMicPaused(!!r.data.mic_paused);
      setMicError(r.data.mic_error && r.data.mic_error_at && Date.now() - new Date(r.data.mic_error_at).getTime() < 10 * 60 * 1000 ? `${r.data.mic_error}${r.data.mic_error_app ? ` · app ${r.data.mic_error_app}` : ""}` : "");
      const incoming = (r.data.chunks || []).filter((c) => !seen.current.has(c.id));
      if (!incoming.length) return;
      incoming.forEach((c) => seen.current.add(c.id));
      lastSeq.current = Math.max(lastSeq.current, ...incoming.map((c) => c.seq));
      setChunks((c) => [...c, ...incoming]);
      if (liveRef.current) { queue.current.push(...incoming); playNext(); }
    } catch {}
  }, [api, bookingId]);

  useEffect(() => {
    lastSeq.current = -1; queue.current = []; seen.current = new Set(); setChunks([]); setNowPlaying(null);
    playingRef.current = false;
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, [bookingId, poll]);

  const toggleLive = () => {
    const nv = !live; setLive(nv); liveRef.current = nv;
    if (nv) { queue.current = [...chunks]; playNext(); }
  };

  // Real-time stream (LiveKit): phone mic -> officer browser, sub-second delay.
  const [rt, setRt] = useState("off"); // off | connecting | waiting | live | error
  const rtRoom = useRef(null);
  const toggleRealtime = async () => {
    if (rtRoom.current) { try { await rtRoom.current.disconnect(); } catch {} rtRoom.current = null; setRt("off"); return; }
    setRt("connecting");
    try {
      const { Room, RoomEvent, Track } = await import("livekit-client");
      const { data } = await api.get(`/officer/trips/${bookingId}/live-token`);
      const room = new Room({ adaptiveStream: false, dynacast: false });
      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === Track.Kind.Audio) { const el = track.attach(); el.autoplay = true; document.body.appendChild(el); setRt("live"); }
      });
      room.on(RoomEvent.TrackUnsubscribed, (track) => { track.detach().forEach((el) => el.remove()); setRt("waiting"); });
      room.on(RoomEvent.Disconnected, () => { rtRoom.current = null; setRt("off"); });
      await room.connect(data.url, data.token);
      await room.startAudio();
      rtRoom.current = room;
      const hasPhone = [...room.remoteParticipants.values()].some((p) => p.audioTrackPublications.size > 0);
      setRt(hasPhone ? "live" : "waiting");
    } catch (e) { setRt("error"); setErr(e?.response?.data?.detail || "Couldn't start the live stream."); }
  };
  useEffect(() => () => { if (rtRoom.current) rtRoom.current.disconnect().catch(() => {}); }, []);
  const onEnded = () => { playingRef.current = false; setNowPlaying(null); if (liveRef.current) setTimeout(playNext, 50); };

  const toggleRecord = async () => {
    const nv = !recording;
    setRecording(nv);
    try {
      await api.post(`/officer/trips/${bookingId}/record`, { on: nv });
      if (nv) {
        const cutoff = Date.now() - 2 * 60 * 1000;
        setChunks((cs) => cs.map((c) => (new Date(c.created_at).getTime() >= cutoff ? { ...c, saved: true } : c)));
      }
    }
    catch { setRecording(!nv); setErr("Couldn't change recording. Try again."); }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-[#0F172A] p-4" data-testid="officer-audio">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-sm font-semibold text-white"><Mic className="h-4 w-4 text-sky-400" />Trip audio ({chunks.length} segments)</p>
        <div className="flex items-center gap-2">
          <button onClick={toggleRecord} data-testid="officer-record-toggle"
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${recording ? "bg-red-600 text-white" : "border border-red-500 text-red-400"}`}>
            <span className={`h-2 w-2 rounded-full ${recording ? "bg-white animate-pulse" : "bg-red-500"}`} />{recording ? "Recording — stop" : "Record"}
          </button>
          <button onClick={toggleLive}
            data-testid="officer-clips-toggle"
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${live ? "bg-slate-600 text-white" : "border border-slate-500 text-slate-300"}`}>
            <Radio className="h-3.5 w-3.5" />{live ? "Playing clips…" : "Play clips"}
          </button>
          <button onClick={toggleRealtime} disabled={rt === "connecting"}
            data-testid="officer-live-toggle"
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${rt === "live" ? "bg-emerald-600 text-white" : rt === "off" || rt === "error" ? "bg-sky-600 text-white" : "bg-sky-800 text-white"}`}>
            <span className={`h-2 w-2 rounded-full ${rt === "live" ? "bg-white animate-pulse" : "bg-white/60"}`} />
            {rt === "off" || rt === "error" ? "Listen live" : rt === "connecting" ? "Connecting…" : rt === "waiting" ? "Live — waiting for phone…" : "LIVE — stop"}
          </button>
        </div>
      </div>
      {rt === "waiting" && (
        <p className="mt-2 rounded-md border border-sky-500/30 bg-sky-500/10 px-2.5 py-1.5 text-xs text-sky-300" data-testid="officer-live-waiting-note">
          Connected to the live room. Waiting for the renter's phone to stream — it joins automatically while monitoring is on (needs app 1.6.1 (latest update) or newer).
        </p>
      )}
      {micPaused && (
        <p className="mt-2 flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-300" data-testid="officer-mic-paused-note">
          <PhoneCall className="h-3.5 w-3.5" />Renter on a call. The phone has handed the mic to the call, so the audio will be silent until the call ends.
        </p>
      )}
      {micError && (
        <p className="mt-2 rounded-md border border-red-500/30 bg-red-500/10 px-2.5 py-1.5 text-xs text-red-300" data-testid="officer-mic-error-note">
          Phone reports a microphone problem: {micError}. Ask the renter to open the app, keep it unlocked for a moment, and check that Microphone is allowed for HooreCars.
        </p>
      )}
      <p className="mt-1 text-[11px] text-neutral-500">{recording ? "Audio is being saved to the urgent folder for 90 days." : "Live only — clips auto-delete after the trip unless you press Record."}</p>
      <audio ref={audioRef} onEnded={onEnded} controls className="mt-3 w-full" data-testid="officer-audio-player" />
      <div className="mt-2 flex items-center gap-2">
        <span className="text-[11px] text-neutral-500">Speed</span>
        {[1, 2, 4, 8].map((s) => (
          <button key={s} onClick={() => { setSpeed(s); if (audioRef.current) audioRef.current.playbackRate = s; }} data-testid={`officer-speed-${s}`}
            className={`rounded px-2 py-0.5 text-[11px] font-semibold ${speed === s ? "bg-sky-600 text-white" : "bg-white/10 text-neutral-300"}`}>{s}x</button>
        ))}
      </div>
      {err ? <p className="mt-1 text-xs text-amber-400">{err}</p> : null}
      <div className="mt-3 max-h-48 space-y-1 overflow-y-auto">
        {chunks.length === 0 && <p className="text-xs text-neutral-500">No audio captured yet. Clips appear here as they arrive from the driver's phone.</p>}
        {[...chunks].reverse().map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-md bg-white/5 px-3 py-1.5 text-xs text-neutral-300">
            <span>#{c.seq + 1} · {fmtDT(c.started_at || c.created_at)}{c.saved ? " · saved" : ""}{c.mic_busy ? <span className="text-amber-400" data-testid={`officer-chunk-call-${c.seq}`}> · renter on a call</span> : null}{typeof c.peak_db === "number" ? <span className={c.peak_db <= -60 ? "text-neutral-500" : "text-emerald-400"} data-testid={`officer-chunk-level-${c.seq}`}> · {c.peak_db <= -60 ? "silent" : `${Math.round(c.peak_db)} dB`}</span> : null}</span>
            <span className="flex items-center gap-3">
              <button onClick={() => playBlob(c)} className="text-sky-400" data-testid={`officer-play-${c.seq}`}>{nowPlaying === c.id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}</button>
              <a onClick={async (e) => { e.preventDefault(); const r = await api.get(`/officer/audio/${c.id}`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = `trip-${bookingId}-${c.seq + 1}.m4a`; a.click(); }} href="#" className="text-neutral-400"><Download className="h-4 w-4" /></a>
            </span>
          </div>
        ))}
      </div>
      {chunks.some((c) => c.saved) && (
        <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/5 p-3" data-testid="officer-saved-folder">
          <p className="text-xs font-semibold text-red-300">Urgent — saved recordings ({chunks.filter((c) => c.saved).length})</p>
          <div className="mt-1 max-h-32 space-y-1 overflow-y-auto">
            {chunks.filter((c) => c.saved).reverse().map((c) => (
              <div key={c.id} className="flex items-center justify-between text-xs text-neutral-300">
                <span>#{c.seq + 1} · {fmtDT(c.started_at || c.created_at)}</span>
                <button onClick={() => playBlob(c)} className="text-sky-400"><Play className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TripPanel({ trip }) {
  const api = useApi();
  const [loc, setLoc] = useState(trip.location);
  const [live, setLive] = useState(trip.live);
  useEffect(() => {
    setLoc(trip.location); setLive(trip.live);
    const t = setInterval(async () => {
      try { const r = await api.get(`/officer/trips/${trip.booking_id}?after_seq=999999`); setLoc(r.data.location); setLive(r.data.live); } catch {}
    }, 10000);
    return () => clearInterval(t);
  }, [trip.booking_id]); // eslint-disable-line
  const hasPos = loc && loc.lat != null;
  return (
    <div className="space-y-4" data-testid="officer-trip-panel">
      <div>
        <h2 className="text-xl font-bold text-white">{trip.car_name}</h2>
        <p className="text-sm text-neutral-400">{trip.customer_name} · {trip.customer_phone}</p>
        <p className="text-xs text-neutral-500">Started {fmtDT(trip.started_at)} · return by {fmtDT(trip.return_at)} · consent {trip.consent ? "on record ✓" : "not recorded"}</p>
      </div>
      <div className="rounded-xl border border-white/10 bg-[#0F172A] p-4">
        <p className="mb-2 flex items-center justify-between text-sm font-semibold text-white"><span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-sky-400" />Live location</span><span className={live ? "text-emerald-400 text-xs" : "text-amber-400 text-xs"}>{live ? "● Live" : "Signal lost"}</span></p>
        {hasPos ? (
          <>
            {MAPS_KEY && <div className="overflow-hidden rounded-lg"><GoogleTripMap lat={loc.lat} lng={loc.lng} label={trip.car_name} /></div>}
            <div className="mt-2 flex items-center justify-between text-xs text-neutral-300">
              <span data-testid="officer-coords">{loc.lat.toFixed(6)}, {loc.lng.toFixed(6)}</span>
              <a href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`} target="_blank" rel="noreferrer" className="text-sky-400 underline">Open in Google Maps</a>
            </div>
          </>
        ) : <p className="text-xs text-neutral-500">Waiting for a GPS signal from the driver's phone.</p>}
      </div>
      <LiveAudio bookingId={trip.booking_id} initialRecording={trip.recording} />
      <RenterDetails userId={trip.user_id} />
    </div>
  );
}

function RenterDetails({ userId }) {
  const api = useApi();
  const [photos, setPhotos] = useState([]);
  const [contacts, setContacts] = useState(null);
  const [srcs, setSrcs] = useState({});
  const [show, setShow] = useState(false);
  const [zoom, setZoom] = useState(null);
  useEffect(() => {
    if (!userId) return;
    api.get(`/officer/users/${userId}/photos`).then((r) => setPhotos(r.data.photos)).catch(() => {});
    api.get(`/officer/users/${userId}/contacts`).then((r) => setContacts(r.data)).catch(() => {});
  }, [userId]); // eslint-disable-line
  useEffect(() => {
    let revoked = [];
    (async () => {
      const out = {};
      for (const p of photos) {
        try { const r = await api.get(`/officer/files/${p.id}`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); out[p.id] = u; revoked.push(u); } catch {}
      }
      setSrcs(out);
    })();
    return () => revoked.forEach((u) => URL.revokeObjectURL(u));
  }, [photos]); // eslint-disable-line
  if (!userId) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-[#0F172A] p-4" data-testid="officer-renter-details">
      <button onClick={() => setShow((s) => !s)} data-testid="officer-renter-toggle" className="flex w-full items-center justify-between text-sm font-semibold text-white">
        <span className="flex items-center gap-2"><User className="h-4 w-4 text-sky-400" />Renter ID & contacts</span>
        <span className="text-xs text-neutral-400">{show ? "Hide" : `${photos.length} photos · ${contacts?.count ?? 0} contacts`}</span>
      </button>
      {show && (
        <div className="mt-3 space-y-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-neutral-500">Photos on file</p>
            {photos.length === 0 ? <p className="mt-1 text-xs text-neutral-500">No photos on file.</p> : (
              <div className="mt-2 grid grid-cols-3 gap-2">
                {photos.map((p) => (
                  <button key={p.id} onClick={() => zoom === p.id ? setZoom(null) : setZoom(p.id)} className="overflow-hidden rounded-lg border border-white/10" data-testid={`officer-photo-${p.doc_type}`}>
                    {srcs[p.id] ? <img src={srcs[p.id]} alt={p.label} className="h-24 w-full object-cover" /> : <div className="flex h-24 items-center justify-center text-[10px] text-neutral-500">Loading…</div>}
                    <p className="truncate bg-black/40 px-1 py-0.5 text-[10px] text-neutral-300">{p.label}</p>
                  </button>
                ))}
              </div>
            )}
            {zoom && srcs[zoom] && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={() => setZoom(null)} data-testid="officer-photo-zoom">
                <img src={srcs[zoom]} alt="" className="max-h-full max-w-full rounded-lg" />
              </div>
            )}
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-neutral-500">Phone contacts {contacts?.updated_at ? `· shared ${fmtDT(contacts.updated_at)}` : ""}</p>
            {!contacts || contacts.count === 0 ? <p className="mt-1 text-xs text-neutral-500">No contacts shared from this phone yet.</p> : (
              <div className="mt-2 max-h-48 space-y-1 overflow-y-auto">
                {contacts.contacts.map((c, i) => (
                  <div key={`${c.number}-${i}`} className="flex items-center justify-between rounded-md bg-white/5 px-3 py-1.5 text-xs" data-testid={`officer-contact-${i}`}>
                    <span className="text-neutral-200">{c.name || "—"}</span>
                    <a href={`tel:${c.number}`} className="text-sky-400">{c.number}</a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Console() {
  const api = useApi();
  const { user } = useUser();
  const [data, setData] = useState(null);
  const [allowed, setAllowed] = useState(null);
  const [sel, setSel] = useState(null);

  const load = useCallback(async () => {
    try {
      await api.get("/officer/me");
      setAllowed(true);
      const r = await api.get("/officer/active");
      setData(r.data.trips);
      setSel((s) => s ? r.data.trips.find((t) => t.booking_id === s.booking_id) || s : null);
    } catch (e) {
      if (e?.response?.status === 403) setAllowed(false);
    }
  }, [api]);

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  if (allowed === false) return (
    <div className="grid min-h-screen place-items-center bg-[#0B1120] p-6" data-testid="officer-denied">
      <div className="max-w-md rounded-2xl border border-white/10 bg-[#0F172A] p-8 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-sky-400" />
        <h1 className="mt-4 text-2xl font-semibold text-white">Not authorized</h1>
        <p className="mt-2 text-sm text-neutral-400">This account does not have Security &amp; Compliance access.</p>
        <SignOutButton><button className="mt-6 rounded-md border border-white/20 px-4 py-2 text-sm text-white">Sign out</button></SignOutButton>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B1120] text-white" data-testid="officer-console">
      <header className="flex items-center justify-between border-b border-white/10 px-6 py-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-sky-400">Security &amp; Compliance</p>
          <h1 className="text-lg font-bold">Live Trip Monitoring</h1>
        </div>
        <div className="flex items-center gap-4 text-sm text-neutral-400">
          <button onClick={load} className="flex items-center gap-1.5 hover:text-white"><RefreshCw className="h-4 w-4" />Refresh</button>
          <span>{user?.primaryEmailAddress?.emailAddress}</span>
          <SignOutButton><button className="rounded-md border border-white/20 px-3 py-1.5">Sign out</button></SignOutButton>
        </div>
      </header>
      <div className="grid gap-6 p-6 lg:grid-cols-[320px_1fr]">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Active trips ({data?.length || 0})</p>
          {data?.length === 0 && <p className="rounded-lg border border-white/10 bg-[#0F172A] p-4 text-sm text-neutral-500" data-testid="officer-empty">No active trips right now.</p>}
          {(data || []).map((t) => (
            <button key={t.booking_id} onClick={() => setSel(t)} data-testid={`officer-trip-${t.booking_id}`}
              className={`w-full rounded-xl border p-3 text-left ${sel?.booking_id === t.booking_id ? "border-sky-500 bg-sky-500/10" : "border-white/10 bg-[#0F172A] hover:border-white/30"}`}>
              <p className="font-semibold">{t.car_name}</p>
              <p className="text-xs text-neutral-400">{t.customer_name}</p>
              <p className="mt-1 flex items-center gap-2 text-[11px]">
                <span className={t.live ? "text-emerald-400" : "text-amber-400"}>● {t.live ? "live" : "no signal"}</span>
                <span className="text-neutral-500">· {t.audio_count} audio</span>
              </p>
            </button>
          ))}
        </div>
        <div>{sel ? <TripPanel key={sel.booking_id} trip={sel} /> : <p className="text-neutral-500">Select an active trip to view live location and listen to audio.</p>}</div>
      </div>
    </div>
  );
}

export default function OfficerConsole() {
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) return <div className="grid min-h-screen place-items-center bg-[#0B1120] text-neutral-400">Loading…</div>;
  if (!isSignedIn) return <OfficerSignInPage />;
  return <Console />;
}
